import { parseNumberStrict, removeNulls } from 'shouldve-been-js';
import type AppTheme from '../Enums/AppTheme';
import LocalStorageKey from '../Enums/LocalStorageKey';
import type Mode from '../Enums/Mode';
import TokenType from '../Enums/TokenType';
import CurrentUser from '../Models/CurrentUserModel';
import { datetimeStandardParser, datetimeToApiString } from '../Utils/DateTimeParser';

class LocalStorageService {

    private _id: string;

    public constructor(id: string) {
        this._id = id;
    }

    public get id(): string {
        return this._id;
    }

    public set id(value: string) {
        this._id = value;
    }

    public static isLocalStorageAvailable(): boolean {
    
        const test: string = 'test';
        
        try {
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch(e) {
            return false;
        }
    }

    public static clearAllData(): void {

        localStorage.clear();
    }

    public getMode(): Mode | null {

        try {
            const stringMode: string | null = localStorage.getItem(this._id + LocalStorageKey.MODE);

            if (stringMode === null) {
                return null;
            }

            return JSON.parse(stringMode);
        } catch (e: unknown) {
            return null;
        }
    }

    public setMode(mode: Mode): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.MODE, JSON.stringify(mode));
            
            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearMode(): boolean {

        try {   
            localStorage.removeItem(this._id + LocalStorageKey.MODE);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getTheme(): AppTheme | null {

        try {
            const themeString: string | null = localStorage.getItem(this._id + LocalStorageKey.THEME);

            if (themeString === null) {
                return null;
            }

            return JSON.parse(themeString);
        } catch (e: unknown) {
            return null;
        }
    }

    public setTheme(theme: AppTheme): boolean {

        try { 
            localStorage.setItem(this._id + LocalStorageKey.THEME, JSON.stringify(theme));

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearTheme(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.THEME);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getCurrentUser(): CurrentUser | null {

        try {
            const userId: string | null = localStorage.getItem(this._id + LocalStorageKey.USER_ID);
            const username: string | null = localStorage.getItem(this._id + LocalStorageKey.USERNAME);
            const email: string | null = localStorage.getItem(this._id + LocalStorageKey.EMAIL);
            const accessString: string | null = localStorage.getItem(this._id + LocalStorageKey.ACCESS);
            const signature: string | null = localStorage.getItem(this._id + LocalStorageKey.SIGNATURE);
            
            if (userId === null || userId === '' || username === null || username === '' || email === null || accessString === null || signature === null) {
                return null;
            }

            const access: string[] = accessString.split(',');
            
            return new CurrentUser(userId, username, email, access, signature);
        } catch (e: unknown) {
            return null;
        }
    }

    public setCurrentUser(currentUser: CurrentUser | null): boolean {

        try {
            if (currentUser === null) {
                localStorage.setItem(this._id + LocalStorageKey.USER_ID, '');
                localStorage.setItem(this._id + LocalStorageKey.USERNAME, '');
                localStorage.setItem(this._id + LocalStorageKey.EMAIL, '');
                localStorage.setItem(this._id + LocalStorageKey.ACCESS, '');
                localStorage.setItem(this._id + LocalStorageKey.SIGNATURE, '');
            } else {
                localStorage.setItem(this._id + LocalStorageKey.USER_ID, currentUser.userId);
                localStorage.setItem(this._id + LocalStorageKey.USERNAME, currentUser.username);
                localStorage.setItem(this._id + LocalStorageKey.EMAIL, currentUser.email);
                localStorage.setItem(this._id + LocalStorageKey.ACCESS, currentUser.access.join(',')); 
                localStorage.setItem(this._id + LocalStorageKey.SIGNATURE, currentUser.signature);
            }

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearCurrentUser(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.USER_ID);
            localStorage.removeItem(this._id + LocalStorageKey.USERNAME);
            localStorage.removeItem(this._id + LocalStorageKey.EMAIL);
            localStorage.removeItem(this._id + LocalStorageKey.ACCESS);
            localStorage.removeItem(this._id + LocalStorageKey.SIGNATURE);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getLastActivity(): Date | null {

        try {
            const lastActivityString: string | null = localStorage.getItem(this._id + LocalStorageKey.LAST_ACTIVITY);
            
            return datetimeStandardParser(lastActivityString, 0);
        } catch (e: unknown) {
            return null;
        }
    }

    public setLastActivity(lastActivity: Date | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.LAST_ACTIVITY, lastActivity === null ? '' : datetimeToApiString(lastActivity, 0));

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearLastActivity(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.LAST_ACTIVITY);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getToken(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.TOKEN);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setToken(token: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.TOKEN, token ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearToken(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.TOKEN);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getRefreshToken(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.REFRESH_TOKEN);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setRefreshToken(refreshToken: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.REFRESH_TOKEN, refreshToken ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearRefreshToken(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.REFRESH_TOKEN);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getExpiration(): Date | null {

        try {
            const stringExpiration: string | null = localStorage.getItem(this._id + LocalStorageKey.EXPIRATION);

            return datetimeStandardParser(stringExpiration, 0);
        } catch (e: unknown) {
            return null;
        }
    }

    public setExpiration(date: Date | null): boolean {

        try {
            if (date === null) {
                localStorage.setItem(this._id + LocalStorageKey.EXPIRATION, '');
            } else {
                localStorage.setItem(this._id + LocalStorageKey.EXPIRATION, datetimeToApiString(date, 0));
            }

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearExpiration(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.EXPIRATION);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getTokenType(): TokenType {

        try {
            const stringTokenType: string | null = localStorage.getItem(this._id + LocalStorageKey.TOKEN_TYPE);

            if (stringTokenType === null) {
                return TokenType.TENANT;
            }

            return JSON.parse(stringTokenType);
        } catch (e: unknown) {
            return TokenType.TENANT;
        }
    }

    public setTokenType(tokenType: TokenType): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.TOKEN_TYPE, JSON.stringify(tokenType));
            
            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearTokenType(): boolean {

        try {   
            localStorage.removeItem(this._id + LocalStorageKey.TOKEN_TYPE);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getTenantDisplayName(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.TENANT_DISPLAY_NAME);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setTenantDisplayName(tenantDisplayName: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.TENANT_DISPLAY_NAME, tenantDisplayName ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearTenantDisplayName(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.TENANT_DISPLAY_NAME);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getApiBaseUrl(): string | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.API_BASE_URL);

            if (value === '') {
                return null;
            }

            return value;
        } catch (e: unknown) {
            return null;
        }
    }

    public setApiBaseUrl(baseUrl: string | null): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.API_BASE_URL, baseUrl ?? '');

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearApiBaseUrl(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.API_BASE_URL);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getOrderSorting(): [string, 'asc' | 'desc'] | null {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.ORDER_SORTING);

            if (value === null || value === '') {
                return null;
            }

            const splitValue: string[] = value.split(';');

            if (splitValue.length !== 2) {
                return null;
            }

            if (splitValue[1] === 'asc') {
                return [splitValue[0] ?? '', 'asc'];
            } else if (splitValue[1] === 'desc') {
                return [splitValue[0] ?? '', 'desc'];
            } else {
                return null;
            }
        } catch (e: unknown) {
            return null;
        }
    }

    public setOrderSorting(field: string, direction: 'asc' | 'desc'): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.ORDER_SORTING, field + ';' + direction);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearOrderSorting(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.ORDER_SORTING);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public getOrderVesselFiltering(): number[] {

        try {
            const value: string | null = localStorage.getItem(this._id + LocalStorageKey.ORDER_VESSEL_FILTERING);

            if (value === null || value === '') {
                return [];
            }

            const vesselIds: (number | null)[] = value.split(';').map((vesselId: string) => parseNumberStrict(vesselId));

            return removeNulls(vesselIds);
        } catch (e: unknown) {
            return [];
        }
    }

    public setOrderVesselFiltering(vesselIds: number[]): boolean {

        try {
            localStorage.setItem(this._id + LocalStorageKey.ORDER_VESSEL_FILTERING, vesselIds.map((vesselId: number) => vesselId.toString()).join(';'));

            return true;
        } catch (e: unknown) {
            return false;
        }
    }

    public clearOrderVesselFiltering(): boolean {

        try {
            localStorage.removeItem(this._id + LocalStorageKey.ORDER_VESSEL_FILTERING);

            return true;
        } catch (e: unknown) {
            return false;
        }
    }
}

export default LocalStorageService;